import React, { useMemo, useState } from "react";

const SmsBlendCalculator = () => {
  const [sell, setSell] = useState("0.0015");
  const [profit, setProfit] = useState("20");
  const [r1, setR1] = useState("0.0017");
  const [r2, setR2] = useState("0");

  const result = useMemo(() => {
    const S = parseFloat(sell) || 0;
    const P = (parseFloat(profit) || 0) / 100;
    const C1 = parseFloat(r1) || 0;
    const C2 = parseFloat(r2) || 0;

    const BC = S / (1 + P);
    const pm = BC > 0 ? ((S - BC) / BC) * 100 : 0;

    const denom = C1 - C2;

    if (denom === 0) {
      return {
        BC,
        pm,
        status: {
          color: "#d29922",
          background: "rgba(210,153,34,0.08)",
          border: "rgba(210,153,34,0.24)",
          text: "⚠ Both routes have the same cost — blend ratio is irrelevant.",
        },
        showBlend: false,
        cheapW: 0,
        expPct: 0,
      };
    }

    const X = (C1 - BC) / denom;
    const expPct = 1 - X;

    if (X < 0) {
      return {
        BC,
        pm,
        status: {
          color: "#15803d",
          background: "rgba(21,128,61,0.08)",
          border: "rgba(21,128,61,0.20)",
          text: "✓ Route 1 alone is already profitable — no blending needed.",
        },
        showBlend: false,
        cheapW: 0,
        expPct: 0,
      };
    }

    if (X > 1) {
      return {
        BC,
        pm,
        status: {
          color: "#dc2626",
          background: "rgba(220,38,38,0.08)",
          border: "rgba(220,38,38,0.20)",
          text: "✗ Impossible — sell price is too low even with a free cheap route.",
        },
        showBlend: false,
        cheapW: 0,
        expPct: 0,
      };
    }

    const cheapW = Math.min(100, Math.max(0, X * 100));

    return {
      BC,
      pm,
      status: null,
      showBlend: true,
      cheapW,
      expPct,
    };
  }, [sell, profit, r1, r2]);

  const inputBase =
    "mt-2 w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm font-medium text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] outline-none transition duration-200 placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100";

  return (
    <section className="relative isolate overflow-hidden rounded-[32px] border border-slate-200/70 bg-gradient-to-br from-white via-slate-50 to-indigo-50/60 px-5 py-6 shadow-[0_20px_80px_rgba(15,23,42,0.08)] sm:px-8 sm:py-8 lg:px-10">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-20 top-0 h-56 w-56 rounded-full bg-blue-200/20 blur-3xl" />
        <div className="absolute right-0 top-12 h-64 w-64 rounded-full bg-indigo-200/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-52 w-52 rounded-full bg-cyan-200/20 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.04)_1px,transparent_1px)] bg-[size:36px_36px] opacity-40" />
      </div>

      <div className="relative mx-auto max-w-5xl">
        <div className="mb-8 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-indigo-600 shadow-sm">
              Route Blending Calculator
            </div>
            <h1 className="mt-4 text-3xl font-semibold tracking-[-0.03em] text-slate-900 sm:text-4xl">
              SMS Blend Optimizer
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-600 sm:text-base">
              Premium SMS cost blending calculator for route planning, target
              margin validation, and instant profitability checks.
            </p>
            <div className="mt-5 h-px w-full max-w-md bg-gradient-to-r from-indigo-500 via-sky-400 to-transparent" />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:min-w-[320px]">
            <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-[0_10px_30px_rgba(15,23,42,0.06)] backdrop-blur">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Blended Cost
              </div>
              <div className="mt-2 text-2xl font-semibold tracking-[-0.02em] text-indigo-600">
                ${result.BC.toFixed(6)}
              </div>
            </div>
            <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-[0_10px_30px_rgba(15,23,42,0.06)] backdrop-blur">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Profit Margin
              </div>
              <div className="mt-2 text-2xl font-semibold tracking-[-0.02em] text-emerald-600">
                {result.pm.toFixed(2)}%
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="space-y-6">
            <div className="rounded-[28px] border border-slate-200/70 bg-white/85 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur sm:p-6">
              <div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Inputs
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Sell Price ($)
                  </span>
                  <input
                    className={inputBase}
                    type="number"
                    value={sell}
                    step="0.0001"
                    onChange={(e) => setSell(e.target.value)}
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Target Profit (%)
                  </span>
                  <input
                    className={inputBase}
                    type="number"
                    value={profit}
                    step="1"
                    onChange={(e) => setProfit(e.target.value)}
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Route 1 Cost ($)
                  </span>
                  <input
                    className={inputBase}
                    type="number"
                    value={r1}
                    step="0.0001"
                    onChange={(e) => setR1(e.target.value)}
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Route 2 Cost ($)
                  </span>
                  <input
                    className={inputBase}
                    type="number"
                    value={r2}
                    step="0.0001"
                    onChange={(e) => setR2(e.target.value)}
                  />
                </label>
              </div>
            </div>

            <div className="rounded-[28px] border border-slate-200/70 bg-white/85 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur sm:p-6">
              <div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Results
              </div>

              {result.status && (
                <div
                  className="mb-5 rounded-2xl border px-4 py-3 text-sm font-medium"
                  style={{
                    color: result.status.color,
                    background: result.status.background,
                    borderColor: result.status.border,
                  }}
                >
                  {result.status.text}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-5 shadow-[0_10px_30px_rgba(99,102,241,0.08)]">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Blended Cost Target
                  </div>
                  <div className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-indigo-600">
                    ${result.BC.toFixed(6)}
                  </div>
                </div>

                <div className="rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-[0_10px_30px_rgba(16,185,129,0.08)]">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Actual Profit Margin
                  </div>
                  <div className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-emerald-600">
                    {result.pm.toFixed(2)}%
                  </div>
                </div>
              </div>

              {result.showBlend && (
                <div className="mt-6 rounded-3xl border border-slate-200 bg-slate-50/80 p-5">
                  <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Blend Ratio
                  </div>

                  <div className="overflow-hidden rounded-full border border-slate-200 bg-white shadow-inner">
                    <div className="flex h-11 w-full overflow-hidden rounded-full">
                      <div
                        className="flex items-center justify-center bg-gradient-to-r from-blue-500 to-cyan-400 text-[11px] font-semibold text-white transition-[width] duration-300"
                        style={{ width: `${result.cheapW}%` }}
                      >
                        {result.cheapW > 10
                          ? `${result.cheapW.toFixed(2)}%`
                          : ""}
                      </div>
                      <div className="flex flex-1 items-center justify-center bg-gradient-to-r from-rose-500 to-red-400 text-[11px] font-semibold text-white">
                        {result.expPct > 0.1
                          ? `${(result.expPct * 100).toFixed(2)}%`
                          : ""}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                      <span className="h-3 w-3 rounded-[4px] bg-blue-500" />
                      <span className="text-sm text-slate-600">
                        Route 2 (Cheap):{" "}
                        <span className="font-semibold text-slate-900">
                          {result.cheapW.toFixed(2)}%
                        </span>
                      </span>
                    </div>

                    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                      <span className="h-3 w-3 rounded-[4px] bg-red-500" />
                      <span className="text-sm text-slate-600">
                        Route 1 (Expensive):{" "}
                        <span className="font-semibold text-slate-900">
                          {(result.expPct * 100).toFixed(2)}%
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-[28px] border border-slate-200/70 bg-white/85 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur sm:p-6">
              <div className="mb-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                Formula Reference
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Blended Cost
                  </div>
                  <div className="mt-2 font-mono text-sm font-semibold text-indigo-600">
                    BC = S ÷ (1 + P)
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Cheap Route %
                  </div>
                  <div className="mt-2 font-mono text-sm font-semibold text-indigo-600">
                    X = (C1 − BC) ÷ (C1 − C2)
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Expensive Route %
                  </div>
                  <div className="mt-2 font-mono text-sm font-semibold text-indigo-600">
                    1 − X
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmsBlendCalculator;
